import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Sidebar = ({ isCollapsed = false, onToggleCollapse }) => {
  const location = useLocation();
  const [expandedGroups, setExpandedGroups] = useState({
    trading: true,
    analytics: true,
    system: true
  });

  const navigationGroups = [
    {
      id: 'overview',
      label: 'Overview',
      items: [
        {
          label: 'Dashboard',
          path: '/trading-dashboard-overview',
          icon: 'BarChart3',
          description: 'Real-time portfolio overview'
        }
      ]
    },
    {
      id: 'trading',
      label: 'Trading Operations',
      items: [
        {
          label: 'Live Feed',
          path: '/live-trading-feed',
          icon: 'TrendingUp',
          description: 'Real-time trading activity'
        },
        {
          label: 'Trade Execution',
          path: '/trade-execution-management',
          icon: 'Zap',
          description: 'Manage trade orders'
        }
      ]
    },
    {
      id: 'analytics',
      label: 'Analytics & Risk',
      items: [
        {
          label: 'Portfolio Analytics',
          path: '/portfolio-performance-analytics',
          icon: 'PieChart',
          description: 'Performance analysis'
        },
        {
          label: 'Risk Assessment',
          path: '/risk-assessment-dashboard',
          icon: 'Shield',
          description: 'Risk monitoring'
        }
      ]
    },
    {
      id: 'system',
      label: 'System Management',
      items: [
        {
          label: 'Market Data Hub',
          path: '/market-data-integration-hub',
          icon: 'Database',
          description: 'Data integration status'
        }
      ]
    }
  ];

  const isActive = (path) => location?.pathname === path;

  const handleNavigation = (path) => {
    window.location.href = path;
  };

  const toggleGroup = (groupId) => {
    if (!isCollapsed) {
      setExpandedGroups(prev => ({
        ...prev,
        [groupId]: !prev?.[groupId]
      }));
    }
  };

  return (
    <aside className={`fixed left-0 top-16 bottom-0 z-40 bg-card border-r border-border transition-all duration-300 ${
      isCollapsed ? 'w-16' : 'w-64'
    }`}>
      <div className="flex flex-col h-full">
        {/* Collapse Toggle */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          {!isCollapsed && (
            <span className="text-sm font-medium text-muted-foreground">
              Navigation
            </span>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleCollapse}
            iconName={isCollapsed ? "ChevronRight" : "ChevronLeft"}
            iconSize={16}
            className="text-muted-foreground hover:text-foreground"
          >
          </Button>
        </div>

        {/* Navigation Content */}
        <div className="flex-1 overflow-y-auto py-4">
          <nav className="space-y-6">
            {navigationGroups?.map((group) => (
              <div key={group?.id} className="px-3">
                {/* Group Header */}
                {!isCollapsed && (
                  <button
                    onClick={() => toggleGroup(group?.id)}
                    className="w-full flex items-center justify-between px-2 py-1 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-smooth"
                  >
                    <span>{group?.label}</span>
                    <Icon 
                      name={expandedGroups?.[group?.id] ? "ChevronDown" : "ChevronRight"} 
                      size={12} 
                    />
                  </button>
                )}

                {/* Group Items */}
                <div className={`space-y-1 ${
                  !isCollapsed && !expandedGroups?.[group?.id] ? 'hidden' : ''
                }`}>
                  {group?.items?.map((item) => (
                    <button
                      key={item?.path}
                      onClick={() => handleNavigation(item?.path)}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium transition-smooth focus-ring group ${
                        isActive(item?.path)
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                      title={isCollapsed ? item?.label : ''}
                    >
                      <Icon 
                        name={item?.icon} 
                        size={16} 
                        className={`flex-shrink-0 ${
                          isActive(item?.path) ? 'text-primary-foreground' : ''
                        }`}
                      />
                      {!isCollapsed && (
                        <div className="flex-1 text-left">
                          <div className="font-medium">{item?.label}</div>
                          <div className="text-xs opacity-75 mt-0.5">
                            {item?.description}
                          </div>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </nav>
        </div>

        {/* System Status */}
        <div className="border-t border-border p-4">
          <div className={`flex items-center space-x-3 ${isCollapsed ? 'justify-center' : ''}`}>
            <div className="w-2 h-2 bg-success rounded-full animate-pulse-subtle"></div>
            {!isCollapsed && (
              <div className="flex-1">
                <div className="text-xs font-medium text-foreground">System Status</div>
                <div className="text-xs text-success">All systems operational</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;